import React from 'react';
import {Box, Button, CircularProgress, CssBaseline, Paper, Step, StepLabel, Stepper, Typography, useTheme} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {ChevronLeftRounded, LockOutlined} from '@mui/icons-material';
import {NavLink} from 'react-router-dom';
import {CardElement} from '@stripe/react-stripe-js';
import Logo from '../utils/Logo';
import ThemeSwitch from '../layout/ThemeSwitch';
import {getSettingsColors, settingsInputStyles} from '../settings/SettingsDesign';

export function AuthLayout({children, session = false, wide = false, compact = false}) {
    return <Box sx={{minHeight: '100dvh', width: '100%', minWidth: 0, bgcolor: 'background.default', color: 'text.primary'}}>
        <CssBaseline/>
        <Box component="header" sx={{maxWidth: 1200, mx: 'auto', px: {xs: 2, sm: 3}, height: 76,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
            <Logo width={74}/>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <ThemeSwitch/>
                {session && <Button component={NavLink} to="/logout" color="inherit" size="small"
                    sx={{borderRadius: '8px', color: 'text.secondary'}}>Log out</Button>}
            </Box>
        </Box>
        <Box component="main" sx={{maxWidth: wide ? 1200 : 688, minWidth: 0, boxSizing: 'border-box', mx: 'auto', px: {xs: 2, sm: 3},
            pt: compact ? {xs: 1, sm: 1.5} : {xs: 3, sm: 6}, pb: 6}}>
            {children}
        </Box>
    </Box>;
}

export function AuthCard({title, description, children, maxWidth = 640, sx = [], ...props}) {
    return <Paper {...props} elevation={0} sx={[{
        ...settingsInputStyles,
        position: 'relative', width: '100%', maxWidth, minWidth: 0, boxSizing: 'border-box', mx: 'auto', p: {xs: 2.5, sm: 4},
        bgcolor: theme => getSettingsColors(theme).card, border: '1px solid', borderColor: 'divider',
        borderRadius: '16px', boxShadow: 'none', overflowWrap: 'anywhere',
        '& .MuiTypography-root': {whiteSpace: 'normal'},
        '& .MuiDivider-root': {bgcolor: 'transparent', borderColor: 'divider', '&::before, &::after': {borderColor: 'divider'}},
        '& .MuiButton-root': {borderRadius: '8px', minHeight: 40, textTransform: 'none', boxShadow: 'none'},
        '& .MuiButton-outlinedInherit': {borderColor: 'divider', color: 'text.primary'},
        '& .MuiButton-root.Mui-focusVisible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2},
        '& .MuiDialogContent-root': {p: 0, overflow: 'visible'},
        '& .MuiDialogActions-root': {px: 0, pt: 3, pb: 0, gap: 1, border: 0, flexWrap: 'wrap'},
        '& .auth-billing-fields': {display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 1.5,
            '& > .MuiBox-root': {p: 0, minWidth: 0}, '& > .MuiBox-root:first-of-type': {gridColumn: '1 / -1'}},
        '& .MuiAlert-root': {borderRadius: '10px', fontSize: 13, lineHeight: 1.65, mb: 2},
        '& .MuiAlert-filledWarning': {bgcolor: theme => alpha(theme.palette.warning.main, 0.14),
            color: theme => theme.palette.mode === 'dark' ? theme.palette.warning.light : '#8a4500'},
        '& .MuiAlert-filledInfo': {bgcolor: theme => alpha(theme.palette.info.main, 0.12), color: 'text.primary'},
        '& .MuiAlert-icon': {color: 'inherit'},
        '& .StripeElement': {m: 0, p: 1.5, minHeight: 46, boxSizing: 'border-box', bgcolor: theme => getSettingsColors(theme).card,
            border: '1px solid', borderColor: 'divider', borderRadius: '8px', boxShadow: 'none'},
        '& .StripeElement--focus': {borderColor: 'primary.main', outline: 'none'},
        '& .StripeElement--invalid': {borderColor: 'error.main'}
    }, ...(Array.isArray(sx) ? sx : [sx])]}>
        {title && <Box sx={{mb: 3}}>
            <Typography component="h1" sx={{fontSize: {xs: 24, sm: 28}, lineHeight: 1.25, fontWeight: 700, letterSpacing: '-0.5px'}}>{title}</Typography>
            {description && <Typography sx={{mt: 1, fontSize: 14, fontWeight: 400, color: 'text.secondary', lineHeight: 1.65}}>{description}</Typography>}
        </Box>}
        {children}
    </Paper>;
}

export function AuthSetupSteps({step}) {
    return <Stepper activeStep={step - 1} alternativeLabel sx={{maxWidth: 600, mx: 'auto', mb: 2.5,
        '& .MuiStepLabel-label': {fontSize: {xs: 11, sm: 13}, whiteSpace: 'normal'},
        '& .MuiStepConnector-line': {borderColor: 'divider'}}}>
        {['Choose a plan', 'Billing details', 'Confirm subscription'].map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
    </Stepper>;
}

export function AuthBackButton(props) {
    return <Button {...props} type="button" color="inherit" variant="outlined" startIcon={<ChevronLeftRounded/>}>Back</Button>;
}

export function AuthLoading({title = 'Signing you in', description = 'Please wait while we verify your account.'}) {
    return <AuthLayout><AuthCard maxWidth={480} title={title} description={description}>
        <Box role="status" aria-label={title} sx={{display: 'flex', justifyContent: 'center', py: 3}}><CircularProgress size={28}/></Box>
    </AuthCard></AuthLayout>;
}

export function AuthCardField() {
    const theme = useTheme();
    // Stripe expects a font-family value, without CSS declaration priorities.
    const fontFamily = theme.typography.fontFamily.replace(/\s*!important\b/gi, '');
    return <Box sx={{mt: 1}}>
        <CardElement options={{style: {
            base: {fontSize: '14px', fontFamily, color: theme.palette.text.primary,
                iconColor: theme.palette.text.secondary, '::placeholder': {color: theme.palette.text.secondary}},
            invalid: {color: theme.palette.error.main}
        }}}/>
    </Box>;
}

export function AuthPaymentNote() {
    return <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 2, color: 'text.secondary'}}>
        <LockOutlined sx={{fontSize: 14}}/>
        <Typography sx={{fontSize: 12}}>Payments securely processed by <Box component="a" href="https://stripe.com" target="_blank"
            rel="noopener noreferrer" sx={{color: 'inherit'}}>Stripe</Box></Typography>
    </Box>;
}
